import { useEffect, useRef, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { PhoneNumberOwnerType, PhoneNumberType, SimCardSelection } from '../../../common/enums.js';
import { useDebounce } from '../../../common/hooks/useDebounce.js';
import { fieldCantBeEmptyMsg, invalidPhoneNumberMsg, invalidSIMCardNumberMsg, t } from '../../../common/i18n/index.js';
import { scrollTo } from '../../../common/utils/browserUtils.js';
import { getNextAllowedDay } from '../../../common/utils/dateUtils.js';
import { localizeValidationErrorMessage } from '../../../common/utils/simCardUtils.js';
import { SimType } from '../../../generated/api/simType.js';
import { ValidateSimFreeResponse } from '../../../generated/api/validateSimFreeResponse.js';
import { fetchValidatePhoneNumber, fetchValidateSim } from '../utils/subscriptionConfigurationApi.js';
import {
  hasSubscriptionConfigData,
  mapFormStateToOrderItemUnit,
  mapOrderItemUnitToFormState,
  validateDonorEmail,
  validatePhoneNumberFormat,
  validateSimCardNotDuplicate,
  validateSimCardNumberFormat,
  validateTransferDate,
} from '../utils/subscriptionConfigurationUtils.js';
import type { ValidatedPhoneNumber } from '../../../common/utils/phoneNumberUtils.js';
import type { ValidatePortableResponse } from '../../../generated/api/validatePortableResponse.js';
import type { PlaceOrderFormData, PlaceOrderItemUnit } from '../placeOrderTypes.js';
import type { SubscriptionConfigFormState } from '../utils/subscriptionConfigurationUtils.js';

/**
 * Internal form structure for subscription configuration sub-components.
 */
export interface InternalFormData {
  configuration: {
    [cartItemInstanceId: string]: {
      selectPhoneNumber?: {
        type: PhoneNumberType;
        existingPhoneNumber?: string;
        newPhoneNumber?: string;
        transferDate?: string;
        numberOwner?: {
          type: PhoneNumberOwnerType;
          permitDonoringOnFixedContract?: boolean;
          personEmail?: string;
        };
      };
      numberPublicity: 'true' | 'false';
      simCardConfiguration?: {
        simType: SimType | SimCardSelection.ACTIVATE_EXISTING;
        simCardNumber?: string;
      };
    };
  };
}

export interface UseSubscriptionConfigFormResult {
  innerFormMethods: ReturnType<typeof useForm<InternalFormData>>;
  formState: SubscriptionConfigFormState;
  fieldsToShow: {
    showSelectPhoneNumber: boolean;
    showNumberPrivacy: boolean;
    showSimCard: boolean;
    showM2MSimCard: boolean;
  };
  errors: Record<string, string>;
  validatedPhoneNumbers: ValidatedPhoneNumber[];
  allOrderItemUnits: PlaceOrderItemUnit[];
  validateAllFields: () => Promise<boolean>;
  scrollToFirstError: () => void;
  setSubmitAttempted: (value: boolean) => void;
}

export interface SubscriptionConfigFormBaseOptions {
  cartItemInstanceId: string;
  basketItemId: string;
  mdmId: string;
  phoneNumbers: string[];
  holidays: Date[];
  fieldsToShow: {
    showSelectPhoneNumber: boolean;
    showNumberPrivacy: boolean;
    showSimCard: boolean;
    showM2MSimCard: boolean;
  };
  getDefaultFormStateFallback?: () => SubscriptionConfigFormState;
  localizePhoneError: (result: ValidatePortableResponse) => string | undefined;
  isNewPhoneRequired: (state: SubscriptionConfigFormState, phoneNumbers: string[]) => boolean;
}

const getFieldPathMap = (cartItemInstanceId: string): Record<string, string> => ({
  existingPhoneNumber: `configuration.${cartItemInstanceId}.selectPhoneNumber.existingPhoneNumber`,
  newPhoneNumber: `configuration.${cartItemInstanceId}.selectPhoneNumber.newPhoneNumber`,
  transferDate: `configuration.${cartItemInstanceId}.selectPhoneNumber.transferDate`,
  donorEmail: `configuration.${cartItemInstanceId}.selectPhoneNumber.numberOwner.personEmail`,
  simCardNumber: `configuration.${cartItemInstanceId}.simCardConfiguration.simCardNumber`,
});

const useSubscriptionInnerForm = (
  cartItemInstanceId: string,
  holidays: Date[],
  currentOrderItem: PlaceOrderItemUnit | undefined,
  getDefaultFormStateFallback: (() => SubscriptionConfigFormState) | undefined
) => {
  const getDefaultFormState = () => {
    const state = hasSubscriptionConfigData(currentOrderItem)
      ? mapOrderItemUnitToFormState(currentOrderItem!, getNextAllowedDay(holidays))
      : (getDefaultFormStateFallback?.() as SubscriptionConfigFormState);

    return {
      configuration: {
        [cartItemInstanceId]: {
          selectPhoneNumber: {
            type: state.phoneNumberType,
            existingPhoneNumber: state.existingPhoneNumber,
            newPhoneNumber: state.newPhoneNumber,
            transferDate: state.transferDate,
            numberOwner: {
              type: state.differentDonor ? PhoneNumberOwnerType.PERSON : PhoneNumberOwnerType.COMPANY,
              permitDonoringOnFixedContract: state.fixedTermContract,
              personEmail: state.donorEmail,
            },
          },
          numberPublicity: state.numberPublicity ? 'true' : 'false',
          simCardConfiguration: {
            simType: state.simType,
            simCardNumber: state.simCardNumber,
          },
        },
      },
    } as InternalFormData;
  };

  const defaultFormStateRef = useRef<InternalFormData | null>(null);
  if (!defaultFormStateRef.current) {
    defaultFormStateRef.current = getDefaultFormState();
  }

  const innerFormMethods = useForm<InternalFormData>({
    mode: 'all',
    shouldFocusError: true,
    defaultValues: defaultFormStateRef.current,
  });

  useEffect(() => {
    if (hasSubscriptionConfigData(currentOrderItem)) {
      innerFormMethods.reset(getDefaultFormState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return innerFormMethods;
};

const useFormStateFromConfig = (
  innerFormMethods: ReturnType<typeof useForm<InternalFormData>>,
  cartItemInstanceId: string,
  holidays: Date[],
  currentOrderItem: PlaceOrderItemUnit | undefined,
  getDefaultFormStateFallback: (() => SubscriptionConfigFormState) | undefined
) => {
  const currentConfig = useWatch({
    control: innerFormMethods.control,
    name: `configuration.${cartItemInstanceId}`,
  });

  const formState: SubscriptionConfigFormState = (() => {
    if (!currentConfig?.selectPhoneNumber) {
      if (hasSubscriptionConfigData(currentOrderItem)) {
        return mapOrderItemUnitToFormState(currentOrderItem!, getNextAllowedDay(holidays));
      }
      return getDefaultFormStateFallback?.() as SubscriptionConfigFormState;
    }
    const { selectPhoneNumber, numberPublicity, simCardConfiguration } = currentConfig;
    return {
      phoneNumberType: selectPhoneNumber.type ?? PhoneNumberType.NEW,
      newPhoneNumber: selectPhoneNumber.newPhoneNumber,
      existingPhoneNumber: selectPhoneNumber.existingPhoneNumber,
      transferDate: selectPhoneNumber.transferDate,
      differentDonor: selectPhoneNumber.numberOwner?.type === PhoneNumberOwnerType.PERSON,
      donorEmail: selectPhoneNumber.numberOwner?.personEmail,
      fixedTermContract: selectPhoneNumber.numberOwner?.permitDonoringOnFixedContract ?? false,
      numberPublicity: numberPublicity === 'true',
      simType: simCardConfiguration?.simType ?? SimType.PHYSICAL,
      simCardNumber: simCardConfiguration?.simCardNumber,
    };
  })();

  return { formState, currentConfig };
};

const useParentSync = (
  formState: SubscriptionConfigFormState,
  currentConfig: unknown,
  basketItemId: string,
  basketItemIndex: number,
  allOrderItemUnits: PlaceOrderItemUnit[],
  orderItemIndex: number,
  methods: ReturnType<typeof useFormContext<PlaceOrderFormData>>
) => {
  const prevFormStateRef = useRef<string>('');
  const initialSyncDone = useRef(false);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const syncToParentForm = (state: SubscriptionConfigFormState) => {
    const idx = allOrderItemUnits.findIndex(
      (item: PlaceOrderItemUnit) => item.basketItemId === basketItemId && item.basketItemIndex === basketItemIndex
    );
    if (idx === -1) {
      return;
    }
    prevFormStateRef.current = JSON.stringify(state);
    initialSyncDone.current = true;
    const currentUnits = methods.getValues('products.placeOrderItemUnits') ?? [];
    const nextUnits = [...currentUnits];
    nextUnits[idx] = { ...currentUnits[idx], ...mapFormStateToOrderItemUnit(state) } as PlaceOrderItemUnit;
    methods.setValue('products.placeOrderItemUnits', nextUnits, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  };

  const syncToParentFormRef = useRef(syncToParentForm);
  syncToParentFormRef.current = syncToParentForm;

  useEffect(() => {
    if (!currentConfig && initialSyncDone.current) {
      return;
    }
    const formStateJson = JSON.stringify(formState);
    if (formStateJson === prevFormStateRef.current) {
      return;
    }

    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }
    syncTimerRef.current = setTimeout(() => {
      syncToParentFormRef.current(formState);
    }, 300);

    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, [formState, currentConfig, orderItemIndex, methods]);

  const flushSync = (state: SubscriptionConfigFormState) => {
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }
    syncToParentFormRef.current(state);
  };

  return { flushSync };
};

const useFieldValidation = (
  formState: SubscriptionConfigFormState,
  innerFormMethods: ReturnType<typeof useForm<InternalFormData>>,
  cartItemInstanceId: string,
  mdmId: string,
  localizePhoneError: (result: ValidatePortableResponse) => string | undefined
) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [validatedPhoneNumbers, setValidatedPhoneNumbers] = useState<ValidatedPhoneNumber[]>([]);

  const upsertValidatedPhone = (entry: ValidatedPhoneNumber) => {
    setValidatedPhoneNumbers(prev => [...prev.filter(v => v.phoneNumber !== entry.phoneNumber), entry]);
  };

  const validatePhoneNumberAsync = async (phoneNumber: string): Promise<string | undefined> => {
    const formatError = validatePhoneNumberFormat(phoneNumber);
    if (formatError) {
      upsertValidatedPhone({ phoneNumber, valid: false, error: formatError, portable: false });
      return formatError;
    }

    try {
      const result = await fetchValidatePhoneNumber(phoneNumber, mdmId);

      const errorMsg = localizePhoneError(result);
      if (errorMsg) {
        upsertValidatedPhone({ phoneNumber, valid: false, error: errorMsg, portable: false });
        return errorMsg;
      }

      upsertValidatedPhone({ phoneNumber, valid: true, error: undefined, portable: result.portable });
      return undefined;
    } catch {
      const errorMsg = t.YLCX(invalidPhoneNumberMsg);
      upsertValidatedPhone({ phoneNumber, valid: false, error: errorMsg, portable: false });
      return errorMsg;
    }
  };

  const validateSimCardAsync = async (simCardNumber: string): Promise<string | undefined> => {
    const formatError = validateSimCardNumberFormat(simCardNumber);
    if (formatError) {
      return formatError;
    }

    const defaultErrorMsg = t.SDXB(invalidSIMCardNumberMsg);

    try {
      const result = await fetchValidateSim(simCardNumber, mdmId);
      if (!result.free) {
        // If there is a technical error, skip the validation. Otherwise, the user will be stuck and
        // not able to proceed with the order. This matches the old checkout behavior.
        if (result.errorType === ValidateSimFreeResponse.ErrorTypeEnum.TECHNICAL_ERROR) {
          return undefined;
        }
        return result.errorMessage || localizeValidationErrorMessage(result.errorType ?? '') || defaultErrorMsg;
      }
      return undefined;
    } catch {
      // Network/fetch failures are treated like TECHNICAL_ERROR — don't block the user
      return undefined;
    }
  };

  const validatePhoneRef = useRef(async (phoneNumber: string) => {
    const error = await validatePhoneNumberAsync(phoneNumber);
    if (error) {
      setErrors(prev => ({ ...prev, existingPhoneNumber: error }));
      innerFormMethods.trigger(`configuration.${cartItemInstanceId}.selectPhoneNumber.existingPhoneNumber`);
    } else {
      setErrors(prev => {
        const { existingPhoneNumber: _, ...rest } = prev;
        return rest;
      });
    }
  });

  const validateSimRef = useRef(async (simCardNumber: string) => {
    const error = await validateSimCardAsync(simCardNumber);
    const simFieldPath =
      `configuration.${cartItemInstanceId}.simCardConfiguration.simCardNumber` as keyof InternalFormData;
    if (error) {
      setErrors(prev => ({ ...prev, simCardNumber: error }));
      innerFormMethods.setError(simFieldPath, { type: 'validate', message: error });
    } else {
      setErrors(prev => {
        const { simCardNumber: _, ...rest } = prev;
        return rest;
      });
      innerFormMethods.clearErrors(simFieldPath);
    }
  });

  const stableValidatePhone = useRef((phoneNumber: string) => validatePhoneRef.current(phoneNumber));
  const stableValidateSim = useRef((simCardNumber: string) => validateSimRef.current(simCardNumber));

  const debouncedValidatePhone = useDebounce(stableValidatePhone.current, 500);
  const debouncedValidateSim = useDebounce(stableValidateSim.current, 500);

  useEffect(() => {
    if (formState.phoneNumberType === PhoneNumberType.EXISTING && formState.existingPhoneNumber) {
      debouncedValidatePhone(formState.existingPhoneNumber);
    }
  }, [formState.existingPhoneNumber, formState.phoneNumberType, debouncedValidatePhone]);

  useEffect(() => {
    if (formState.simType === SimCardSelection.ACTIVATE_EXISTING) {
      if (formState.simCardNumber) {
        debouncedValidateSim(formState.simCardNumber);
      } else if (submitAttempted) {
        setErrors(prev => ({ ...prev, simCardNumber: t.VPVR(fieldCantBeEmptyMsg) }));
      }
    }
  }, [formState.simCardNumber, formState.simType, debouncedValidateSim, submitAttempted]);

  return {
    errors,
    setErrors,
    setSubmitAttempted,
    validatedPhoneNumbers,
    validatePhoneNumberAsync,
    validateSimCardAsync,
  };
};

interface SubmitValidationOptions {
  formState: SubscriptionConfigFormState;
  fieldsToShow: UseSubscriptionConfigFormResult['fieldsToShow'];
  holidays: Date[];
  allOrderItemUnits: PlaceOrderItemUnit[];
  phoneNumbers: string[];
  cartItemInstanceId: string;
  innerFormMethods: ReturnType<typeof useForm<InternalFormData>>;
  errors: Record<string, string>;
  setErrors: (value: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  setSubmitAttempted: (value: boolean) => void;
  validatePhoneNumberAsync: (phoneNumber: string) => Promise<string | undefined>;
  validateSimCardAsync: (simCardNumber: string) => Promise<string | undefined>;
  isNewPhoneRequired: (state: SubscriptionConfigFormState, phoneNumbers: string[]) => boolean;
  flushSync: (state: SubscriptionConfigFormState) => void;
}

const createSubmitHandlers = ({
  formState,
  fieldsToShow,
  holidays,
  allOrderItemUnits,
  phoneNumbers,
  cartItemInstanceId,
  innerFormMethods,
  errors,
  setErrors,
  setSubmitAttempted,
  validatePhoneNumberAsync,
  validateSimCardAsync,
  isNewPhoneRequired,
  flushSync,
}: SubmitValidationOptions) => {
  const validatePhoneNumberField = async (state: SubscriptionConfigFormState): Promise<Record<string, string>> => {
    const fieldErrors: Record<string, string> = {};

    if (state.phoneNumberType !== PhoneNumberType.EXISTING) {
      if (!state.newPhoneNumber && isNewPhoneRequired(state, phoneNumbers)) {
        fieldErrors.newPhoneNumber = t.VPVR(fieldCantBeEmptyMsg);
      }
      return fieldErrors;
    }

    if (!state.existingPhoneNumber) {
      fieldErrors.existingPhoneNumber = t.VPVR(fieldCantBeEmptyMsg);
      return fieldErrors;
    }

    const phoneError =
      validatePhoneNumberFormat(state.existingPhoneNumber) ||
      (await validatePhoneNumberAsync(state.existingPhoneNumber));
    if (phoneError) {
      fieldErrors.existingPhoneNumber = phoneError;
    }

    return fieldErrors;
  };

  const validateSimCardField = async (
    state: SubscriptionConfigFormState,
    orderItemUnits: PlaceOrderItemUnit[]
  ): Promise<Record<string, string>> => {
    const fieldErrors: Record<string, string> = {};

    if (state.simType !== SimCardSelection.ACTIVATE_EXISTING) {
      return fieldErrors;
    }

    if (!state.simCardNumber) {
      fieldErrors.simCardNumber = t.VPVR(fieldCantBeEmptyMsg);
      return fieldErrors;
    }

    const formatError = validateSimCardNumberFormat(state.simCardNumber);
    if (formatError) {
      fieldErrors.simCardNumber = formatError;
      return fieldErrors;
    }

    const allSimNumbers = orderItemUnits
      .map((u: PlaceOrderItemUnit) => u.simCardNumber)
      .filter((n): n is string => !!n);

    const dupeError = validateSimCardNotDuplicate(state.simCardNumber, allSimNumbers);
    if (dupeError) {
      fieldErrors.simCardNumber = dupeError;
      return fieldErrors;
    }

    const backendError = await validateSimCardAsync(state.simCardNumber);
    if (backendError) {
      fieldErrors.simCardNumber = backendError;
    }

    return fieldErrors;
  };

  const syncErrorsToRhf = (newErrors: Record<string, string>) => {
    const fieldMap = getFieldPathMap(cartItemInstanceId);
    for (const field of Object.keys(fieldMap)) {
      const fieldPath = fieldMap[field] as keyof InternalFormData;
      if (newErrors[field]) {
        innerFormMethods.setValue(fieldPath, innerFormMethods.getValues(fieldPath), {
          shouldTouch: true,
        });
        innerFormMethods.setError(fieldPath, { type: 'validate', message: newErrors[field] });
      } else {
        innerFormMethods.clearErrors(fieldPath);
      }
    }
  };

  const validateAllFields = async (): Promise<boolean> => {
    setSubmitAttempted(true);

    const { showSelectPhoneNumber, showSimCard, showM2MSimCard } = fieldsToShow;
    const newErrors: Record<string, string> = {};

    if (showSelectPhoneNumber) {
      Object.assign(newErrors, await validatePhoneNumberField(formState));

      const dateError = validateTransferDate(formState.transferDate, holidays);
      if (dateError) {
        newErrors.transferDate = dateError;
      }

      const emailError = validateDonorEmail(formState.donorEmail, formState.differentDonor);
      if (emailError) {
        newErrors.donorEmail = emailError;
      }
    }

    if (showSimCard || showM2MSimCard) {
      Object.assign(newErrors, await validateSimCardField(formState, allOrderItemUnits));
    }

    setErrors(newErrors);
    syncErrorsToRhf(newErrors);

    const isValid = Object.keys(newErrors).length === 0;

    if (isValid) {
      // Flush sync to parent form immediately so the data is available before step transition.
      // This prevents the race condition where the 300ms debounced sync hasn't fired yet
      // and the component unmounts (canceling the timer), leaving the parent form without
      // subscription config fields (mobileNumber, numberPorting, simType, etc.).
      flushSync(formState);
    }

    return isValid;
  };

  const scrollToFirstError = () => {
    const errorFieldOrder = ['existingPhoneNumber', 'newPhoneNumber', 'transferDate', 'donorEmail', 'simCardNumber'];
    const fieldMap = getFieldPathMap(cartItemInstanceId);

    for (const field of errorFieldOrder) {
      if (errors[field]) {
        const element =
          document.querySelector<HTMLElement>(`[name="${fieldMap[field]}"]`) ||
          document.getElementById(`select-phone-number-${cartItemInstanceId}`) ||
          document.querySelector<HTMLElement>(`[name*="${field}"]`);

        if (element) {
          scrollTo(element);
          if (element instanceof HTMLInputElement) {
            element.focus();
          }
          break;
        }
      }
    }
  };

  return { validateAllFields, scrollToFirstError };
};

/**
 * Hook to manage subscription configuration form state, validation, and sync with parent form.
 */
export const useSubscriptionConfigFormBase = ({
  cartItemInstanceId,
  basketItemId,
  mdmId,
  phoneNumbers,
  holidays,
  fieldsToShow,
  getDefaultFormStateFallback,
  localizePhoneError,
  isNewPhoneRequired,
}: SubscriptionConfigFormBaseOptions): UseSubscriptionConfigFormResult => {
  const methods = useFormContext<PlaceOrderFormData>();
  const allOrderItemUnits = methods.watch('products.placeOrderItemUnits') || [];

  const parts = cartItemInstanceId.split('-');
  const basketItemIndex = parts.length > 1 ? parseInt(parts[parts.length - 1], 10) : 0;
  const orderItemIndex = allOrderItemUnits.findIndex(
    (item: PlaceOrderItemUnit) => item.basketItemId === basketItemId && item.basketItemIndex === basketItemIndex
  );

  const currentOrderItem = orderItemIndex >= 0 ? allOrderItemUnits[orderItemIndex] : undefined;

  const innerFormMethods = useSubscriptionInnerForm(
    cartItemInstanceId,
    holidays,
    currentOrderItem,
    getDefaultFormStateFallback
  );

  const { formState, currentConfig } = useFormStateFromConfig(
    innerFormMethods,
    cartItemInstanceId,
    holidays,
    currentOrderItem,
    getDefaultFormStateFallback
  );

  const { flushSync } = useParentSync(
    formState,
    currentConfig,
    basketItemId,
    basketItemIndex,
    allOrderItemUnits,
    orderItemIndex,
    methods
  );

  const {
    errors,
    setErrors,
    setSubmitAttempted,
    validatedPhoneNumbers,
    validatePhoneNumberAsync,
    validateSimCardAsync,
  } = useFieldValidation(formState, innerFormMethods, cartItemInstanceId, mdmId, localizePhoneError);

  const { validateAllFields, scrollToFirstError } = createSubmitHandlers({
    formState,
    fieldsToShow,
    holidays,
    allOrderItemUnits,
    phoneNumbers,
    cartItemInstanceId,
    innerFormMethods,
    errors,
    setErrors,
    setSubmitAttempted,
    validatePhoneNumberAsync,
    validateSimCardAsync,
    isNewPhoneRequired,
    flushSync,
  });

  return {
    innerFormMethods,
    formState,
    fieldsToShow,
    errors,
    validatedPhoneNumbers,
    allOrderItemUnits,
    validateAllFields,
    scrollToFirstError,
    setSubmitAttempted,
  };
};
